// ============================================
// Admin Events List
// ============================================

import React from 'react';
import { Link } from 'react-router-dom';
import { collection, query, orderBy, onSnapshot, getDocs, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Event } from '../../types';

export default function AdminEvents() {
  const { user, role } = useAuth();
  const [events, setEvents] = React.useState<Event[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!user) return;

    // For superadmin, load all events directly
    if (role === 'superadmin') {
      const q = query(collection(db, 'events'), orderBy('createdAt', 'desc'));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const allEventsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Event[];
        setEvents(allEventsData);
        setLoading(false);
      });

      return unsubscribe;
    }

    // For regular admin, find events where user is an admin
    const loadAdminEvents = async () => {
      try {
        const userEvents: Event[] = [];
        const seenEventIds = new Set<string>();

        // Load live events (readable by anyone)
        const liveEventsQuery = query(collection(db, 'events'), where('status', '==', 'live'));
        const liveSnapshot = await getDocs(liveEventsQuery);

        for (const doc of liveSnapshot.docs) {
          const eventData = { id: doc.id, ...doc.data() } as Event;
          try {
            const adminSnap = await getDocs(collection(db, 'events', doc.id, 'admins'));
            const isAdmin = adminSnap.docs.some((adminDoc) => adminDoc.id === user.uid);
            if (isAdmin || eventData.createdBy === user.uid) {
              userEvents.push(eventData);
              seenEventIds.add(doc.id);
            }
          } catch (err) {
            // Can't read admins, skip
          }
        }

        // Try draft events
        const draftEventsQuery = query(collection(db, 'events'), where('status', '==', 'draft'));
        try {
          const draftSnapshot = await getDocs(draftEventsQuery);
          for (const doc of draftSnapshot.docs) {
            if (seenEventIds.has(doc.id)) continue;
            const eventData = { id: doc.id, ...doc.data() } as Event;
            try {
              const adminSnap = await getDocs(collection(db, 'events', doc.id, 'admins'));
              const isAdmin = adminSnap.docs.some((adminDoc) => adminDoc.id === user.uid);
              if (isAdmin || eventData.createdBy === user.uid) {
                userEvents.push(eventData);
                seenEventIds.add(doc.id);
              }
            } catch (err) {
              // Can't read admins, skip
            }
          }
        } catch (err) {
          // Can't read draft events
        }

        // Try closed events
        const closedEventsQuery = query(collection(db, 'events'), where('status', '==', 'closed'));
        try {
          const closedSnapshot = await getDocs(closedEventsQuery);
          for (const doc of closedSnapshot.docs) {
            if (seenEventIds.has(doc.id)) continue;
            const eventData = { id: doc.id, ...doc.data() } as Event;
            try {
              const adminSnap = await getDocs(collection(db, 'events', doc.id, 'admins'));
              const isAdmin = adminSnap.docs.some((adminDoc) => adminDoc.id === user.uid);
              if (isAdmin || eventData.createdBy === user.uid) {
                userEvents.push(eventData);
                seenEventIds.add(doc.id);
              }
            } catch (err) {
              // Can't read admins, skip
            }
          }
        } catch (err) {
          // Can't read closed events
        }

        // Sort by most recent first (approximation without createdAt)
        setEvents(userEvents);
      } catch (err) {
        console.error('Error loading events:', err);
      } finally {
        setLoading(false);
      }
    };

    loadAdminEvents();
  }, [role, user]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'live':
        return <span className="badge live">RUNNING</span>;
      case 'draft':
        return <span className="badge draft">PENDING</span>;
      case 'closed':
        return <span className="badge closed">CLOSED</span>;
      default:
        return null;
    }
  };

  const formatDateRange = (startDate?: string, endDate?: string) => {
    if (!startDate && !endDate) return '-';
    if (startDate && endDate) return `${startDate} - ${endDate}`;
    if (startDate) return startDate;
    return endDate;
  };

  return (
    <div className="admin-events">
      <div className="page-header">
        <h1>{role === 'superadmin' ? 'All Events' : 'My Events'}</h1>
        <Link to="/admin/events/new" className="btn btn-primary">
          + New Event
        </Link>
      </div>

      {loading ? (
        <div className="loading">Loading events...</div>
      ) : events.length === 0 ? (
        <div className="empty-state">
          <h2>No events yet</h2>
          <p>Create your first event to get started.</p>
          <Link to="/admin/events/new" className="btn btn-primary">
            Create Event
          </Link>
        </div>
      ) : (
        <div className="events-table">
          <table>
            <thead>
              <tr>
                <th>Event Name</th>
                <th>Venue</th>
                <th>Dates</th>
                <th>Status</th>
                <th>Tables</th>
                <th>Price</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id}>
                  <td>{event.name}</td>
                  <td>{event.venueName}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatDateRange(event.startDate, event.endDate)}</td>
                  <td>{getStatusBadge(event.status)}</td>
                  <td>{event.tablesCount}</td>
                  <td>
                    {event.currency} {event.pricePerMinute}/min
                  </td>
                  <td>
                    <Link to={`/admin/events/${event.id}`} className="btn btn-sm btn-primary">
                      Manage
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
